import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Timestamp, doc, getDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../../config/firebase-config";
import { useUser } from "../../backend/hooks/user/use-user";
import useAuthCallback from "../../backend/hooks/auth/use-auth-callback";
import { AppRoutes } from "../../reusable";
import { useAlerts } from "../../backend/hooks/alert/use-alert";
import { Modal, Loader } from "../../reusable";
import ResumeUploadModal from "../../components/ResumeUploadModal";
import "./job-detail-mobile-styles.css";

function formatPostedTime(createdAt) {
  if (!createdAt) return "";
  const now = Timestamp.now();
  const diffInSeconds = now.seconds - createdAt.seconds;
  const diffDays = Math.floor(diffInSeconds / (60 * 60 * 24));
  return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
}

export default function JobDetailMobile() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const { loggedInUser } = useUser();
  const { addAlert } = useAlerts();
  const [isApplying, setIsApplying] = useState(false);
  const [showResumeModal, setShowResumeModal] = useState(false);

  const protectedLoginRedirect = useAuthCallback(() => {
    navigate(`/jobs/${jobId}`);
  });

  const handleBack = () => {
    navigate(-1);
  };

  const handleApply = async () => {
    if (!loggedInUser?.email) {
      protectedLoginRedirect();
      return;
    }
    const userProfileRef = doc(db, "creator-profiles", loggedInUser.userId);
    const userProfileSnap = await getDoc(userProfileRef);
    if (!userProfileSnap.exists()) {
      navigate(AppRoutes.FILL_PROFILE_DETAILS.replace(":userId", loggedInUser.userId));
      return;
    }
    const userProfile = userProfileSnap.data();
    if (!userProfile.lookingForJob) {
      addAlert({
        type: "error",
        message: "Please update your profile and set job seeking toggle on to apply for jobs.",
        autoHide: true
      });
      return;
    }
    if (!userProfile.resume) {
      setShowResumeModal(true);
      return;
    }
    setIsApplying(true);
    try {
      const functions = getFunctions();
      const sendJobApplication = httpsCallable(functions, 'sendJobApplication');
      await sendJobApplication({
        jobId,
        userId: loggedInUser.userId
      });

      addAlert({
        type: "success",
        message: "Application submitted successfully!",
        autoHide: true
      });
    } catch (error) {
      console.error("Error applying for job:", error);
      let errorMsg = "Failed to submit application. Please try again.";
      if (error?.message?.includes("You have already applied to this job.")) {
        errorMsg = "You have already applied to this job.";
      } else if (error?.code === 'functions/already-exists' && error?.details) {
        errorMsg = error.details;
      }
      addAlert({
        type: "error",
        message: errorMsg,
        autoHide: true
      });
    }
    setIsApplying(false);
  };

  useEffect(() => {
    let isMounted = true;
    async function fetchJobDetail() {
      if (!jobId) {
        setJob(null);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const jobDocRef = doc(db, "job-postings", jobId);
        const docSnap = await getDoc(jobDocRef);
        if (docSnap.exists() && isMounted) {
          const data = docSnap.data();
          setJob({
            id: docSnap.id,
            title: data.jobPosition || '',
            company: data.companyName || '',
            location: (data.jobLocation && data.jobLocation.place) || '',
            locationType: data.jobType || '',
            logo: data.profilePicture || '/favicon.svg',
            posted: formatPostedTime(data.createdAt),
            description: data.jobDescription || '',
            jobPosition: data.jobPosition || '',
            jobSalary: data.jobSalary || '',
            jobType: data.jobType || '',
            requiredEducation: data.requiredEducation || '',
            requiredExperience: data.requiredExperience || '',
            requiredSoftwareSkills: data.requiredSoftwareSkills || '',
            ...data
          });
        } else if (isMounted) {
          setJob(null);
        }
      } catch (err) {
        if (isMounted) setJob(null);
      }
      if (isMounted) setLoading(false);
    }
    fetchJobDetail();
    return () => { isMounted = false; };
  }, [jobId]);

  return (      
    <main className="kydoscope-job-detail-mobile-main">
      <button onClick={handleBack} className="kydoscope-back-button">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        Jobs
      </button>
      {loading ? (
        <div></div>
      ) : !job ? (
        <div className="kydoscope-no-jobs">Job not found</div>
      ) : (
        <div className="kydoscope-job-detail-mobile-content">
          <div style={{display: 'flex', justifyContent: 'left', alignItems: 'flex-end', marginBottom: 18, marginTop: 0, paddingTop: 0}}>
            <div style={{textAlign: 'left'}}>
              <div className="kydoscope-job-detail-mobile-title" style={{fontSize: '1.02 rem'}}>{job.title}</div>
              <div className="kydoscope-job-detail-mobile-location">{job.location}{job.locationType ? ` (${job.locationType})` : ''}</div>
            </div>
          </div>
          <div className="kydoscope-job-detail-mobile-company" style={{marginBottom: 18}}>
            <img src={job.logo} alt={job.company} className="kydoscope-job-detail-mobile-logo" style={{width: 54, height: 54}} loading="lazy" />
            <div style={{fontWeight: 600, fontSize: '1.13rem', paddingTop: 12}}>{job.company}</div>
          </div>
          <div className="kydoscope-job-section-title" style={{marginBottom: 12, marginTop: 22, fontSize: '1.02 rem'}}>Job Details</div>
          <div className="kydoscope-job-section-content" style={{marginBottom: 18, fontSize: '1 rem'}}>
            <dl style={{margin: 0, padding: 0}}>
              <div style={{display: 'flex', marginBottom: -2}}>
                <dt style={{minWidth: 12, fontWeight: 600, marginRight: 8}}>Position:</dt>
                <dd style={{margin: 0, fontSize: '13.7px'}}>{job.jobPosition}</dd>
              </div>
              <div style={{display: 'flex', marginBottom: -2}}>
                <dt style={{minWidth: 12, fontWeight: 600, marginRight: 8}}>Salary:</dt>
                <dd style={{margin: 0, fontSize: '13.7px'}}>{job.jobSalary}</dd>
              </div>
              <div style={{display: 'flex', marginBottom: 8}}>
                <dt style={{minWidth: 12, fontWeight: 600, marginRight: 8}}>Type:</dt>
                <dd style={{margin: 0, fontSize: '13.7px'}}>{job.jobType}</dd>
              </div>
            </dl>
          </div>
          <div className="kydoscope-job-section-title" style={{marginBottom: 12, marginTop: 22, fontSize: '1.02 rem'}}>Job Description</div>
          <div className="kydoscope-job-section-content" style={{marginBottom: 18, fontSize: '1.08 rem', whiteSpace: 'pre-wrap'}}>{job.description}</div>
          <div className="kydoscope-job-section-title" style={{marginBottom: 12, marginTop: 22, fontSize: '1.02 rem'}}>Job Requirements</div>
          <div className="kydoscope-job-section-content" style={{marginBottom: 18, fontSize: '1 rem'}}>
            <dl style={{margin: 0, padding: 0}}>
              <div style={{display: 'flex', marginBottom: -2}}>
                <dt style={{minWidth: 12, fontWeight: 550, marginRight: 8}}>Education:</dt>
                <dd style={{margin: 0, fontSize:'13.7px'}}>{job.requiredEducation}</dd>
              </div>
              <div style={{display: 'flex', marginBottom: -2}}>
                <dt style={{minWidth: 12, fontWeight: 550, marginRight: 8}}>Experience:</dt>
                <dd style={{margin: 0, fontSize:'13.7px'}}>{job.requiredExperience}</dd>
              </div>
              <div style={{display: 'flex'}}>
                <dt style={{minWidth: 12, fontWeight: 550, marginRight: 8}}>Software Skills:</dt>
                <dd style={{margin: 0, fontSize:'13.7px'}}>{job.requiredSoftwareSkills}</dd>
              </div>              
            </dl>
            <button 
              className="kydoscope-job-detail-mobile-apply-btn" 
              style={{fontSize: '1.13rem', maxWidth: 240, marginTop: 32}}
              onClick={handleApply}
              disabled={isApplying}
            >
              {isApplying ? (<><span className="spinner" style={{marginRight:8}}></span>Applying...</>) : "Apply now"}
            </button>
          </div>
        </div>
      )}
      {isApplying && (
        <Modal isOpen={isApplying} onClose={() => {}} title={null}>
          <div className="modal-content" style={{ textAlign: "center" }}>
            Please wait while we process your application!
            <div style={{ marginTop: 24 }}>
              <Loader />
            </div>
          </div>
        </Modal>
      )}
      {showResumeModal && (
        <ResumeUploadModal
          isOpen={showResumeModal}
          onClose={() => { setShowResumeModal(false); }}
          userId={loggedInUser?.userId}
          onSuccess={() => {
            setShowResumeModal(false);
            // After successful upload, try to apply again
            setTimeout(() => handleApply(), 0);
          }}
        />
      )}
    </main>
  );
}
